<template>
  <BittsPopover
    :mount-to-body="true"
    trigger="click"
    placement="bottomRight"
    class="report-overflow-menu"
    @click.stop
  >
    <FontAwesomeIcon
      :icon="['fak', 'menu']"
      :style="{ height: '12px', width: '12px' }"
      class="report-overflow-menu__button"
    />
    <template #content>
      <div class="flex flex-col text-neutral-text-strong my-4">
        <RouterLink
          v-if="!errors.length && !trialOver && !isFreeTier"
          :to="duplicateReportRoute"
          class="report-overflow-menu__overflow-item"
        >
          <FontAwesomeIcon
            :icon="['fak', 'copy']"
            class="report-overflow-menu__overflow-icon"
          />
          Duplicate
        </RouterLink>
        <button
          v-if="!hideExport && !errors.length"
          class="report-overflow-menu__overflow-item"
          @click="onExport"
          type="button"
        >
          <FontAwesomeIcon
            v-if="!errors.length"
            :icon="['fak', 'download']"
            class="report-overflow-menu__overflow-icon"
          />
          Export
        </button>
        <RouterLink
          v-if="!isPublicReport && canShareReport"
          class="report-overflow-menu__overflow-item"
          :to="shareReportRoute"
          type="button"
        >
          <FontAwesomeIcon
            :icon="['fak', 'share']"
            class="report-overflow-menu__overflow-icon"
          />
          Share
        </RouterLink>
        <RouterLink
          :to="deleteReportRoute"
          class="report-overflow-menu__overflow-item text-danger-text"
        >
          <FontAwesomeIcon
            :icon="['fak', 'delete']"
            class="report-overflow-menu__overflow-icon text-danger-accent"
          />
          Delete
        </RouterLink>
      </div>
    </template>
  </BittsPopover>
</template>

<script setup lang="ts">
import { BittsPopover } from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { DateTime } from 'luxon';
import { computed, ref } from 'vue';
import { RouteParamsRaw, useRoute } from 'vue-router';

import { getExpandedSegmentFilterOverrides } from '@/composables/reports/segmentFilterHelpers';
import useExport from '@/composables/useExport';
import useIteratively from '@/composables/useIteratively';
import {
  ECOSYSTEM,
  EXPORT_IMPACT_TO_NAME_LOOKUP,
  GREENFIELD_TYPE,
} from '@/constants/reports';
import { CHILD_ROUTES } from '@/constants/routes';
import { useBillingStore, useReportsStore } from '@/stores';
import {
  Report,
  ReportExportPayloadV5,
  ReportFilterGroup,
  TrimmedReport,
} from '@/types/reports';

const {
  errors = [],
  hideExport = false,
  isOnReportsPage = true,
  trialOver = false,
  reportId,
  reportType = '',
  partnerOrgIds = [],
  canShareReport = false,
} = defineProps<{
  errors?: string[];
  hideExport?: boolean;
  isOnReportsPage?: boolean;
  trialOver?: boolean;
  reportId: string;
  reportType?: string;
  partnerOrgIds?: number[];
  canShareReport?: boolean;
}>();

const { performExport } = useExport();
const reportsStore = useReportsStore();
const { isFreeTier } = useBillingStore();
const isExporting = ref(false);

const route = useRoute();

// Get basic report info from the store (list data, no columns/filters)
const trimmedReport = computed(() => reportsStore.getByReportId(reportId));

const isOnPartnerDetailsPage = computed(() => {
  return route.name === 'partner_details';
});
const isOnListsPage = computed(() => {
  return route.name === 'lists';
});

const isPublicReport = computed(
  () => trimmedReport.value?.visibility === 'public',
);

function getRoute(routeName: string, params: RouteParamsRaw = {}) {
  return {
    name: routeName,
    params,
    query: { id: reportId },
  };
}

const deleteRouteName = computed(() => {
  if (isOnPartnerDetailsPage.value) {
    return 'partner_details.report_delete';
  } else if (isOnReportsPage) {
    if (isOnListsPage.value) {
      return 'lists.delete';
    }
    return CHILD_ROUTES.reports.REPORTS_DELETE;
  }
  return CHILD_ROUTES.folder.REPORTS_DELETE;
});

const duplicateRouteName = computed(() => {
  if (isOnPartnerDetailsPage.value) {
    return 'partner_details.report_duplicate';
  } else if (isOnReportsPage) {
    if (isOnListsPage.value) {
      return 'lists.duplicate';
    }
    return CHILD_ROUTES.reports.REPORTS_DUPLICATE;
  }
  return CHILD_ROUTES.folder.REPORTS_DUPLICATE;
});

const shareRouteName = computed(() => {
  if (isOnPartnerDetailsPage.value) {
    return 'partner_details.report_share';
  } else if (isOnReportsPage) {
    if (isOnListsPage.value) {
      return 'lists.share';
    }
    return 'reports.share';
  }
  return 'folder.share';
});

const deleteReportRoute = computed(() => {
  if (isOnPartnerDetailsPage.value) {
    return getRoute(deleteRouteName.value, {
      partner_org_id: route.params.partner_org_id,
      id: reportId,
    });
  }
  if (isOnReportsPage) return getRoute(deleteRouteName.value, { id: reportId });
  return getRoute(deleteRouteName.value, {
    folder_id: trimmedReport.value?.folder_uuid,
  });
});
const duplicateReportRoute = computed(() => {
  if (isOnPartnerDetailsPage.value) {
    return getRoute(duplicateRouteName.value, {
      partner_org_id: route.params.partner_org_id,
      id: reportId,
    });
  }
  if (isOnReportsPage) {
    return getRoute(duplicateRouteName.value, { id: reportId });
  }
  return getRoute(duplicateRouteName.value, {
    folder_id: trimmedReport.value?.folder_uuid,
  });
});
const shareReportRoute = computed(() => {
  if (isOnPartnerDetailsPage.value) {
    return getRoute(shareRouteName.value, {
      partner_org_id: route.params.partner_org_id,
      id: reportId,
    });
  }
  if (isOnReportsPage) {
    return getRoute(shareRouteName.value);
  }
  return getRoute(shareRouteName.value, {
    folder_id: trimmedReport.value?.folder_uuid,
  });
});

function transformFilter(
  filter: ReportFilterGroup & {
    report_id?: string;
    id?: string;
    tableName?: string;
  },
) {
  const newFilter = { ...filter };
  delete newFilter.report_id;
  delete newFilter.id;
  delete newFilter.tableName;
  return newFilter;
}

function buildExportParams(report: Report): ReportExportPayloadV5 {
  let exportName = report.name;
  if (report.partner_impact_scores?.length) {
    // we can only do one at at a time, for now
    const impactFilter = report.partner_impact_scores[0];
    if (impactFilter)
      exportName += ` ${EXPORT_IMPACT_TO_NAME_LOOKUP[impactFilter]}`;
  }
  const params: ReportExportPayloadV5 = {
    consolidated_report_type: report.consolidated_report_type,
    our_population_ids: report.our_population_ids,
    partner_population_ids: report.partner_population_ids,
    columns: report.columns.map((col) => {
      if (!col.source_field_id) {
        return {
          column_order: col.column_order,
          column_type: col.column_type,
        };
      }
      return col;
    }),
    filters: report.filters.map(transformFilter),
    partner_standard_populations: report.partner_standard_populations || [],

    segment_filters: report.segment_filters ? report.segment_filters : {},

    exported_at: DateTime.local().toFormat('f'),
    export_name: exportName,
    report_id: report.id,
  };
  const isAllPartnersType = report.consolidated_report_type === ECOSYSTEM;

  Object.assign(
    params,
    isAllPartnersType && { partner_population_ids: [] },
    report.segments && { segments: report.segments },
    report.partner_segments && {
      partner_segments: report.partner_segments,
      ...(report.partner_organization_ids && {
        partner_organization_ids: report.partner_organization_ids,
      }),
    },
    report.tag_id && { tag_id: report.tag_id },
    report.tag_ids && { tag_ids: report.tag_ids },
    getExpandedSegmentFilterOverrides(report),
  );
  return params;
}

function buildAnalyticsPayload(report: TrimmedReport) {
  const payload: {
    population_id: number[];
    partner_id: number[];
    location: string;
    report_id: string;
    report_name: string;
    report_type: string;
    partner_impact_scores: string[];
    event_site: string;
    greenfield_type?: string;
  } = {
    population_id: report.our_population_ids || [],
    partner_id: partnerOrgIds,
    location: 'ellipsis_out_report',
    report_id: report.id,
    report_name: report.name,
    report_type: reportType,
    partner_impact_scores: report.partner_impact_scores || [],
    event_site: EVENT_SITES.REPORT_OVERFLOW_MENU,
  };
  if (reportType === GREENFIELD_TYPE) {
    payload.greenfield_type = report.consolidated_report_type;
  }
  return payload;
}

const { iteratively } = useIteratively();

async function onExport() {
  if (isExporting.value) return;
  isExporting.value = true;

  try {
    // Fetch full report to get columns and filters (not included in list endpoint)
    const fullReport = await reportsStore.fetchReportById(reportId);
    if (!fullReport) {
      return;
    }

    const params = buildExportParams(fullReport);
    await performExport(params, '/v0.5/report-data/export');
    iteratively.userExportedReport(buildAnalyticsPayload(fullReport));
  } finally {
    isExporting.value = false;
  }
}
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.report-overflow-menu {
  @apply flex items-center justify-end h-full mb-4;
}
.report-overflow-menu__button {
  @apply text-neutral-400 p-4 rounded-bts-sm hover:bg-primary-background-medium/8 hover:bg-blend-overlay;
}
.report-overflow-menu__overflow-icon {
  @apply text-neutral-accent w-16 h-16;
}
.report-overflow-menu__overflow-item {
  @apply cursor-pointer px-8 py-4 mx-4 rounded-bts-sm text-left flex items-center gap-8;
}

.report-overflow-menu__overflow-item:hover {
  @apply bg-neutral-background-weak;
}
</style>
